import React, { forwardRef, useState, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ptBR } from 'date-fns/locale';
import { Calendar } from 'lucide-react';
import { formatCalendarDateToBR, formatCalendarDateForInput } from '../../utils/dateFormatter';

// Register Portuguese (Brazil) locale
registerLocale('pt-BR', ptBR);

interface DateInputProps {
  value?: string; // ISO 8601 datetime string (can be yyyy-mm-dd or full datetime)
  onChange?: (isoDate: string) => void; // Returns full ISO 8601 datetime (e.g., 2006-01-02T15:04:05.000Z)
  disabled?: boolean;
  error?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * Format input value with dd/mm/yyyy mask
 */
const formatDateMask = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Apply mask: dd/mm/yyyy
  let formatted = '';
  if (digits.length > 0) {
    formatted = digits.substring(0, 2); // dd
  }
  if (digits.length >= 3) {
    formatted += '/' + digits.substring(2, 4); // mm
  }
  if (digits.length >= 5) {
    formatted += '/' + digits.substring(4, 8); // yyyy
  }

  return formatted;
};

/**
 * Validate dd/mm/yyyy date string
 */
const validateBRDate = (dateStr: string): { valid: boolean; error?: string } => {
  if (!dateStr) return { valid: true };

  const digits = dateStr.replace(/\D/g, '');

  // Check if complete
  if (digits.length < 8) {
    return { valid: false, error: 'Data incompleta' };
  }

  const day = parseInt(digits.substring(0, 2), 10);
  const month = parseInt(digits.substring(2, 4), 10);
  const year = parseInt(digits.substring(4, 8), 10);

  // Validate ranges
  if (day < 1 || day > 31) {
    return { valid: false, error: 'Dia inválido (deve ser entre 01 e 31)' };
  }
  if (month < 1 || month > 12) {
    return { valid: false, error: 'Mês inválido (deve ser entre 01 e 12)' };
  }
  if (year < 1900 || year > 2100) {
    return { valid: false, error: 'Ano inválido (deve ser entre 1900 e 2100)' };
  }

  // Check if date is valid (e.g., not 31/02/2024)
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { valid: false, error: 'Data inválida (exemplo: 31/02 não existe)' };
  }

  return { valid: true };
};

/**
 * Convert dd/mm/yyyy to Date object
 */
const parseBRDateToDate = (brDate: string): Date | null => {
  const digits = brDate.replace(/\D/g, '');
  if (digits.length !== 8) return null;

  const day = parseInt(digits.substring(0, 2), 10);
  const month = parseInt(digits.substring(2, 4), 10);
  const year = parseInt(digits.substring(4, 8), 10);

  return new Date(year, month - 1, day);
};

/**
 * Convert Date to dd/mm/yyyy string
 */
const formatDateToBR = (date: Date | null): string => {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Custom Date Input Component with Calendar Picker
 * - Forces dd/MM/yyyy format regardless of browser locale
 * - Auto-formats as user types with slashes
 * - Validates date and shows Portuguese error messages
 * - Includes calendar picker
 */
export const DateInput = forwardRef<any, DateInputProps>(
  ({ value, onChange, disabled, error, className, placeholder = 'dd/mm/aaaa', ...props }, ref) => {
    // The incoming value is a CALENDAR date — a Postgres DATE arriving as
    // "1990-05-15T00:00:00Z", where the time is padding. Reading it with `new Date(value)`
    // and local getters showed the previous day everywhere behind UTC (all of Brazil), and
    // re-picking the displayed day then saved it, losing a day per edit round-trip.
    // Read the calendar day lexically instead, and anchor the picker at LOCAL midnight of
    // that same day so it highlights the right cell.
    const calendarISO = formatCalendarDateForInput(value);
    const dateValue = calendarISO
      ? new Date(
          Number(calendarISO.slice(0, 4)),
          Number(calendarISO.slice(5, 7)) - 1,
          Number(calendarISO.slice(8, 10)),
        )
      : null;

    // Internal state for manual input
    const [inputValue, setInputValue] = useState(formatCalendarDateToBR(value));
    const [validationError, setValidationError] = useState<string>('');
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Update input value when prop value changes
    React.useEffect(() => {
      setInputValue(formatCalendarDateToBR(value));
      setValidationError('');
    }, [value]);

    const handlePickerChange = (date: Date | null) => {
      if (date && onChange) {
        const isoDate = date.toISOString();
        onChange(isoDate);
        setInputValue(formatDateToBR(date));
        setValidationError('');
      } else if (!date && onChange) {
        onChange('');
        setInputValue('');
        setValidationError('');
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formatted = formatDateMask(rawValue);
      setInputValue(formatted);

      // Clear previous errors while typing
      setValidationError('');

      // Only validate and update parent when we have a complete date
      const digits = formatted.replace(/\D/g, '');
      if (digits.length === 8) {
        const validation = validateBRDate(formatted);
        if (validation.valid) {
          const date = parseBRDateToDate(formatted);
          if (date && onChange) {
            onChange(date.toISOString());
          }
        } else {
          setValidationError(validation.error || 'Data inválida');
        }
      } else if (digits.length === 0 && onChange) {
        // Empty input
        onChange('');
      }
    };

    const handleBlur = () => {
      // Validate on blur
      if (inputValue) {
        const validation = validateBRDate(inputValue);
        if (!validation.valid) {
          setValidationError(validation.error || 'Data inválida');
        }
      }
    };

    const handleCalendarToggle = () => {
      if (!disabled) {
        setIsCalendarOpen(!isCalendarOpen);
      }
    };

    const handleCalendarClose = () => {
      setIsCalendarOpen(false);
    };

    return (
      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            maxLength={10}
            className={`${className} ${error || validationError ? 'border-red-500' : ''}`}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={handleCalendarToggle}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <Calendar className="w-4 h-4" />
          </button>
        </div>

        {isCalendarOpen && (
          <div className="absolute z-50 mt-1">
            <DatePicker
              selected={dateValue}
              onChange={(date: Date | null) => {
                handlePickerChange(date);
                handleCalendarClose();
              }}
              onClickOutside={handleCalendarClose}
              dateFormat="dd/MM/yyyy"
              locale="pt-BR"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              inline
            />
          </div>
        )}

        {validationError && (
          <p className="text-red-500 text-xs mt-1">{validationError}</p>
        )}
      </div>
    );
  }
);

DateInput.displayName = 'DateInput';

export default DateInput;
